/* Form Component to Add or Edit Customer details*/

import { useState } from "react";
import { Button, Form, Input, Select, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TConfigParam, TResponseModel } from "../../../types/config";
import { eFONTFAMILY, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TServicePartner } from "../../../types";
import {
  AddEditConfigInfo,
  GetConfigGroups,
  GetConfigParamDataList,
} from "../../../utils/api.constant";
import { CONFIG_GROUPS, ISSUE_CATEGORY } from "../../../utils/constants";
import { requiredFieldValidator } from "../../../utils/input.validator";
import {
  CheckOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import CustomModal from "../../../components/modal";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * CustomerForm component is used for adding or editing customer information.
 * It provides a form with fields for customer details such as name, code,
 * address, country, state, region, district, and zipcode. The component
 * handles fetching and setting dropdown options for countries, states,
 * cities, and regions based on the selected values. It also manages form
 * submission to add or update customer records.
 *
 * Props:
 * - id: number - The ID of the customer to edit; 0 for adding a new customer.
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the customer list
 *   after adding or editing a customer.
 */
export default function AddConfigCategoryForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPartners, setSelectedPartners] = useState<TServicePartner[]>(
    []
  );
  const [categoryOptions, setCategoryOptions] = useState<TConfigParam[]>([]);
  const [selectedParam, setSelectedParam] = useState<TConfigParam | null>();
  const [isModalVisible, setModalVisible] = useState<boolean>(false);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  const defaultValues: TConfigParam = {
    id: 0,
    name: "",
    description: "",
    groupName: "",
    groupId: null,
    groupUniqueId: "",
    paramUniqueId: "",
  };

  const getCategoryParameters = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      setIsLoading(true);

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setCategoryOptions(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: any) => {
    try {
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          name: values.name,
          description: values.name,
          groupUniqueId: values.categoryTypeid,
        },
      };

      setIsLoading(true);

      const response = await post(AddEditConfigInfo, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        setSelectedPartners([]);
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedParam(null);
    setModalVisible(false);
  };

  // After final confirmation from user
  const handleOk = async () => {
    try {
      const payload = {
        data: {
          id: selectedParam?.id,
          isDeleted: 1,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditConfigInfo, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        await getCategoryParameters(form.getFieldValue("categoryTypeid"));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
      setModalVisible(false);
      setSelectedParam(null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="categoryTypeid"
                label="Category Type"
                rules={[{ required: true, message: "Select category type" }]}
              >
                <Select
                  placeholder="Please select category type"
                  onChange={async (value: string, option: any) =>
                    await getCategoryParameters(value)
                  }
                >
                  <Select.Option value={CONFIG_GROUPS.ISSUECATEGORY}>
                    Issue Category
                  </Select.Option>
                  <Select.Option value={CONFIG_GROUPS.SUBISSUECATEGORY}>
                    Sub Issue Category
                  </Select.Option>
                  <Select.Option value={CONFIG_GROUPS.SUBISSUECATEGORY1}>
                    Sub Issue Category 1
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="name"
                label="Category Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter category name"),
                  },
                ]}
              >
                <Input placeholder="Enter category name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              {categoryOptions?.map((category: TConfigParam) => (
                <div
                  style={{
                    // height: "2rem",
                    padding: "6px 10px",
                    display: "flex",
                    justifyContent: "space-between",
                    background: "#FFF",
                  }}
                >
                  <div>
                    <span
                      style={{
                        color: "#333",
                        fontFamily: eFONTFAMILY.MEDIUM,
                        fontSize: "14px",
                      }}
                    >
                      {category?.name}
                    </span>
                  </div>
                  <div>
                    {category?.paramUniqueId !== ISSUE_CATEGORY.DISPENSER &&
                    category?.paramUniqueId !== ISSUE_CATEGORY.GYRO &&
                    category?.paramUniqueId !== ISSUE_CATEGORY.HARDWARED &&
                    category?.paramUniqueId !== ISSUE_CATEGORY.HARDWAREG &&
                    category?.paramUniqueId !== ISSUE_CATEGORY.SOFTWARED &&
                    category?.paramUniqueId !== ISSUE_CATEGORY.SOFTWAREG ? (
                      <CloseCircleOutlined
                        style={{
                          color: "#333",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSelectedParam(category);
                          setModalVisible(true);
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              {props.id ? "Update" : "Save"}
            </Button>
            <Button
              type="primary"
              className="btnDimensions"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={async () => await handleOk()}
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete {""}
          <b>{selectedParam?.name}</b>
        </p>
      </CustomModal>
    </FormLayout>
  );
}
